import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createUrithiAsset, deleteUthiriAsset, fetchUrithiAssets,
  IMG_BASE_URL, updateUrithiAsset,
  fetchAssetCategories, fetchQuantityUnits,
} from './hooks/hooks';
import {
  Upload, Edit2, Trash2, Plus, Search, ChevronDown,
  TrendingUp, Calculator, Package, Layers,
  Home as HomeIcon, Car, DollarSign, Calendar, Percent,
  FileText, Tag, X as XIcon,
} from 'lucide-react';
import {
  Toast, LoadingState, ErrorState, Pagination,
  BackButton, ConfirmModal, StatusBadge, URITHI,
} from './ui';

// ─── Empty form (stable identity, outside component) ─────────────────────────
const EMPTY_FORM = {
  asset_name: '',
  description: '',
  condition: 'new',
  category_ref_id: '',
  unit_id: '',
  asset_worth: '',
  duration_in_years: '',
  installment_duration_days: '',
  deferred_payment_premium_charges: '',
  available_quantity: '1',
  images: [], // array of File objects, 1 to 4
};

// ─── Reusable bits ───────────────────────────────────────────────────────────
const Field = ({ label, error, children, icon: Icon, hint }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5 text-xs font-bold text-[#001f3f] uppercase tracking-wide">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </label>
    {children}
    {hint && !error && <p className="text-[11px] text-gray-500">{hint}</p>}
    {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
  </div>
);

const inputCls = 'w-full px-3.5 py-2.5 border border-gray-200 text-sm rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 transition-all bg-white placeholder:text-gray-400';

const StatCard = ({ accent, label, value, icon: Icon, small = false }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 relative overflow-hidden">
    <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: accent }} />
    <div className="flex items-center justify-between pl-2">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">{label}</p>
        <p className={`font-black text-[#001f3f] truncate ${small ? 'text-sm' : 'text-2xl'}`}>{value}</p>
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center ml-2 flex-shrink-0" style={{ backgroundColor: accent + '18' }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
    </div>
  </div>
);

const IconBtn = ({ onClick, title, color, children, disabled }) => {
  const colors = {
    navy: 'text-[#001f3f] hover:bg-[#001f3f]/10',
    red: 'text-red-600 hover:bg-red-50',
  };
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${colors[color]}`}>
      {children}
    </button>
  );
};

// ─── Multi-image picker (1–4 files) ─────────────────────────────────────────
const ImagePicker = ({ images, onChange, error }) => {
  const handleAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const combined = [...images, ...files].slice(0, 4); // cap at 4
    onChange(combined);
    e.target.value = ''; // allow re-selecting same file
  };

  const handleRemove = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {images.map((file, idx) => (
          <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50 group">
            <img src={URL.createObjectURL(file)} alt={`upload-${idx}`} className="w-full h-full object-cover" />
            {idx === 0 && (
              <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-[#001f3f] text-white text-[9px] font-bold uppercase tracking-wide">
                Primary
              </span>
            )}
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              className="absolute top-1.5 right-1.5 p-1 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        ))}

        {images.length < 4 && (
          <label className="aspect-square rounded-xl border-2 border-dashed border-gray-200 hover:border-[#001f3f] hover:bg-[#001f3f]/5 cursor-pointer flex flex-col items-center justify-center transition-all">
            <div className="w-10 h-10 rounded-full bg-[#001f3f]/10 flex items-center justify-center mb-1.5">
              <Upload className="w-4 h-4 text-[#001f3f]" />
            </div>
            <span className="text-[11px] font-semibold text-[#001f3f]">Add image</span>
            <span className="text-[10px] text-gray-500">{images.length}/4</span>
            <input type="file" className="hidden" onChange={handleAdd} accept="image/*" multiple />
          </label>
        )}
      </div>
      {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
      {images.length === 0 && !error && (
        <p className="text-[11px] text-gray-500">Upload 1 to 4 images. The first one is the primary image shown on cards.</p>
      )}
    </div>
  );
};

// ─── Asset form ──────────────────────────────────────────────────────────────
const AssetForm = React.memo(({
  isUpdate, formData, errors, calculations,
  categories, units,
  onFieldChange, onImagesChange, onSubmit, isLoading,
}) => {
  // Filter categories to match a selected Mobile/Immobile mode is not needed
  // here — admin picks any active category. Sort by asset_type for readability.
  const groupedCategories = useMemo(() => {
    const groups = { Mobile: [], Immobile: [] };
    (categories || []).forEach((c) => {
      if (groups[c.asset_type]) groups[c.asset_type].push(c);
    });
    return groups;
  }, [categories]);

  return (
    <form onSubmit={onSubmit}>
      <div className="space-y-5">
        {/* Live calculations panel */}
        <div className="bg-[#001f3f] rounded-2xl p-5 text-white">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-4 h-4 text-[#C9982A]" />
            <h3 className="text-xs font-bold uppercase tracking-widest">Live Calculations</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Installments', value: calculations.numberOfInstallments },
              { label: 'Gross/Installment', value: `TZS ${calculations.netPayPerInstallment.toLocaleString()}` },
              { label: 'Total per Cycle', value: `TZS ${calculations.totalPayPerCycle.toLocaleString()}` },
              { label: 'Without Rate', value: `TZS ${calculations.paymentWithoutRate.toLocaleString()}` },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/10 rounded-xl p-3 border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 font-bold mb-1">{stat.label}</p>
                <p className="text-sm font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Section: Basics */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#001f3f] mb-4">Basics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Asset Name" error={errors.asset_name} icon={Package}>
              <input
                type="text"
                value={formData.asset_name}
                onChange={(e) => onFieldChange('asset_name', e.target.value)}
                className={inputCls}
                placeholder="e.g. Toyota Corolla 2018"
              />
            </Field>

            <Field label="Category" error={errors.category_ref_id} icon={Layers} hint="Admin-managed list. Tagged Mobile or Immobile.">
              <div className="relative">
                <select
                  value={formData.category_ref_id}
                  onChange={(e) => onFieldChange('category_ref_id', e.target.value)}
                  className={`${inputCls} appearance-none pr-9`}
                >
                  <option value="">Select category</option>
                  {groupedCategories.Mobile.length > 0 && (
                    <optgroup label="Mobile">
                      {groupedCategories.Mobile.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {groupedCategories.Immobile.length > 0 && (
                    <optgroup label="Immobile">
                      {groupedCategories.Immobile.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </Field>

            <Field label="Condition" error={errors.condition} icon={Tag}>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { val: 'new',    label: 'New' },
                  { val: 'used',   label: 'Second Hand' },
                  { val: 'refurb', label: 'Refurbished' },
                ].map((opt) => {
                  const active = formData.condition === opt.val;
                  return (
                    <button
                      key={opt.val}
                      type="button"
                      onClick={() => onFieldChange('condition', opt.val)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
                        active
                          ? 'border-[#001f3f] bg-[#001f3f] text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Asset Worth" error={errors.asset_worth} icon={DollarSign}>
              <div className="relative">
                <span className="absolute left-3.5 top-2.5 text-xs text-gray-500 font-bold">TZS</span>
                <input
                  type="number"
                  value={formData.asset_worth}
                  onChange={(e) => onFieldChange('asset_worth', e.target.value)}
                  className={`${inputCls} pl-12`}
                  placeholder="0.00"
                />
              </div>
            </Field>

            <Field label="Available Quantity" error={errors.available_quantity} icon={Package}>
              <input
                type="number"
                min="1"
                value={formData.available_quantity}
                onChange={(e) => onFieldChange('available_quantity', e.target.value)}
                className={inputCls}
                placeholder="Units available"
              />
            </Field>

            <Field label="Unit of Measure" error={errors.unit_id} icon={Layers} hint="e.g. piece, kg, acre — leave blank if not applicable">
              <div className="relative">
                <select
                  value={formData.unit_id}
                  onChange={(e) => onFieldChange('unit_id', e.target.value)}
                  className={`${inputCls} appearance-none pr-9`}
                >
                  <option value="">Not specified</option>
                  {(units || []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.symbol ? `(${u.symbol})` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Description" error={errors.description} icon={FileText} hint="Detailed description shown to buyers on the product page">
              <textarea
                value={formData.description}
                onChange={(e) => onFieldChange('description', e.target.value)}
                rows={4}
                className={`${inputCls} resize-y`}
                placeholder="e.g. Well maintained Toyota Corolla 2018, 80,000 km, full service history, single owner…"
              />
            </Field>
          </div>
        </div>

        {/* Section: Payment plan */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#001f3f] mb-4">Payment Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Duration (Years)" error={errors.duration_in_years} icon={Calendar}>
              <input
                type="number"
                step="0.1"
                value={formData.duration_in_years}
                onChange={(e) => onFieldChange('duration_in_years', e.target.value)}
                className={inputCls}
                placeholder="e.g. 0.5, 1.0, 2.5"
              />
            </Field>

            <Field label="Installment Period (Days)" error={errors.installment_duration_days} icon={Calendar}>
              <input
                type="number"
                value={formData.installment_duration_days}
                onChange={(e) => onFieldChange('installment_duration_days', e.target.value)}
                className={inputCls}
                placeholder="Days between payments"
              />
            </Field>

            <Field label="DPPc (Premium)" error={errors.deferred_payment_premium_charges} icon={Percent} hint="0.30 means 30%">
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  max="1"
                  min="0"
                  value={formData.deferred_payment_premium_charges}
                  onChange={(e) => onFieldChange('deferred_payment_premium_charges', e.target.value)}
                  className={`${inputCls} pr-9`}
                  placeholder="0.30"
                />
                <span className="absolute right-3.5 top-2.5 text-xs text-gray-500 font-bold">%</span>
              </div>
            </Field>
          </div>
        </div>

        {/* Section: Images */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#001f3f] mb-4">
            Images {isUpdate && <span className="text-gray-400 normal-case font-medium">(leave empty to keep existing)</span>}
          </h3>
          <ImagePicker images={formData.images} onChange={onImagesChange} error={errors.images} />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#001f3f] hover:bg-[#0a3260] text-white py-3.5 rounded-xl text-sm font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Processing…' : (isUpdate ? 'Update Asset' : 'Create New Asset')}
        </button>
      </div>
    </form>
  );
});

// ─── Main component ─────────────────────────────────────────────────────────
function UrithiNormalAssets() {
  const queryClient = useQueryClient();

  const { data: assets, isLoading, isError, refetch } = useQuery({
    queryKey: ['urithiAssets'],
    queryFn: () => fetchUrithiAssets(),
  });

  const { data: categories } = useQuery({
    queryKey: ['assetCategories'],
    queryFn: () => fetchAssetCategories(),
  });

  const { data: units } = useQuery({
    queryKey: ['quantityUnits'],
    queryFn: fetchQuantityUnits,
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState({ create: false, update: false });
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all | Mobile | Immobile
  const [filterCondition, setFilterCondition] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [updateFormData, setUpdateFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [updateErrors, setUpdateErrors] = useState({});

  const [calculations, setCalculations] = useState({
    numberOfInstallments: 0, netPayPerInstallment: 0, totalPayPerCycle: 0, paymentWithoutRate: 0,
  });
  const [updateCalculations, setUpdateCalculations] = useState({
    numberOfInstallments: 0, netPayPerInstallment: 0, totalPayPerCycle: 0, paymentWithoutRate: 0,
  });

  // ── Filter & sort ──
  const filteredAssets = useMemo(() => {
    if (!Array.isArray(assets)) return [];
    return assets.filter((a) => {
      if (searchQuery && !a.asset_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterType !== 'all') {
        const aType = a.category_ref?.asset_type || a.category;
        if (aType !== filterType) return false;
      }
      if (filterCondition !== 'all' && a.condition !== filterCondition) return false;
      return true;
    });
  }, [assets, searchQuery, filterType, filterCondition]);

  const sortedAssets = useMemo(() =>
    [...filteredAssets].sort((a, b) => {
      if (sortBy === 'asset_name') return a.asset_name.localeCompare(b.asset_name);
      return new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);
    })
  , [filteredAssets, sortBy]);

  // ── Calculation engine ──
  const calc = useCallback((worth, duration, installmentDays, dppc) => {
    if (!worth || !duration || !installmentDays || dppc < 0) {
      return { numberOfInstallments: 0, netPayPerInstallment: 0, totalPayPerCycle: 0, paymentWithoutRate: 0 };
    }
    const durationInDays = duration * 365;
    const numberOfInstallments = Math.floor(durationInDays / installmentDays);
    const totalPayPerCycle = worth + (worth * dppc);
    const netPayPerInstallment = numberOfInstallments > 0 ? totalPayPerCycle / numberOfInstallments : 0;
    const paymentWithoutRate = numberOfInstallments > 0 ? worth / numberOfInstallments : 0;
    return {
      numberOfInstallments,
      netPayPerInstallment: parseFloat(netPayPerInstallment.toFixed(2)),
      totalPayPerCycle: parseFloat(totalPayPerCycle.toFixed(2)),
      paymentWithoutRate: parseFloat(paymentWithoutRate.toFixed(2)),
    };
  }, []);

  // ── Validation ──
  const validateForm = useCallback((data, isUpdate = false) => {
    const e = {};
    if (!data.asset_name?.trim()) e.asset_name = 'Asset name is required';
    else if (data.asset_name.length > 255) e.asset_name = 'Must be 255 characters or less';
    if (!data.category_ref_id) e.category_ref_id = 'Category is required';
    if (!data.condition) e.condition = 'Condition is required';
    if (!data.asset_worth || parseFloat(data.asset_worth) <= 0) e.asset_worth = 'Asset worth must be positive';
    if (!data.duration_in_years || parseFloat(data.duration_in_years) <= 0) e.duration_in_years = 'Duration must be positive';
    if (!data.installment_duration_days || parseInt(data.installment_duration_days) <= 0) e.installment_duration_days = 'Must be positive';
    const dppc = parseFloat(data.deferred_payment_premium_charges);
    if (isNaN(dppc) || dppc < 0 || dppc > 1) e.deferred_payment_premium_charges = 'Must be between 0 and 1';
    const qty = parseInt(data.available_quantity);
    if (isNaN(qty) || qty < 1) e.available_quantity = 'Quantity must be 1 or greater';
    if (!isUpdate && (!data.images || data.images.length < 1)) e.images = 'Upload at least 1 image (max 4)';
    if (data.images && data.images.length > 4) e.images = 'Maximum 4 images allowed';
    return e;
  }, []);

  // ── Recalculate as user types ──
  useEffect(() => {
    setCalculations(calc(
      parseFloat(formData.asset_worth),
      parseFloat(formData.duration_in_years),
      parseInt(formData.installment_duration_days),
      parseFloat(formData.deferred_payment_premium_charges),
    ));
  }, [formData.asset_worth, formData.duration_in_years, formData.installment_duration_days, formData.deferred_payment_premium_charges, calc]);

  useEffect(() => {
    setUpdateCalculations(calc(
      parseFloat(updateFormData.asset_worth),
      parseFloat(updateFormData.duration_in_years),
      parseInt(updateFormData.installment_duration_days),
      parseFloat(updateFormData.deferred_payment_premium_charges),
    ));
  }, [updateFormData.asset_worth, updateFormData.duration_in_years, updateFormData.installment_duration_days, updateFormData.deferred_payment_premium_charges, calc]);

  // ── Field handlers ──
  const handleFieldChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleImagesChange = useCallback((images) => {
    setFormData((prev) => ({ ...prev, images }));
    setErrors((prev) => {
      if (!prev.images) return prev;
      const next = { ...prev };
      delete next.images;
      return next;
    });
  }, []);

  const handleUpdateFieldChange = useCallback((name, value) => {
    setUpdateFormData((prev) => ({ ...prev, [name]: value }));
    setUpdateErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleUpdateImagesChange = useCallback((images) => {
    setUpdateFormData((prev) => ({ ...prev, images }));
    setUpdateErrors((prev) => {
      if (!prev.images) return prev;
      const next = { ...prev };
      delete next.images;
      return next;
    });
  }, []);

  // ── Mutations ──
  const mutation = useMutation({
    mutationFn: createUrithiAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['urithiAssets'] });
      setFormData(EMPTY_FORM);
      setErrors({});
      setOpen({ create: false, update: false });
      setToast({ open: true, message: 'Asset added successfully!', severity: 'success' });
      refetch();
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail
        || err?.response?.data?.images?.[0]
        || err?.response?.data?.category_ref_id?.[0]
        || 'Failed to add asset. Try again.';
      setToast({ open: true, message: msg, severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateUrithiAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(['urithiAssets']);
      setUpdateFormData(EMPTY_FORM);
      setUpdateErrors({});
      setOpen({ create: false, update: false });
      setToast({ open: true, message: 'Asset updated successfully!', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to update asset. Try again.', severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUthiriAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(['urithiAssets']);
      setDeleteConfirm({ open: false, id: null });
      setToast({ open: true, message: 'Asset deactivated successfully', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to delete asset.', severity: 'error' }),
  });

  // ── Submit ──
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const errs = validateForm(formData);
    if (Object.keys(errs).length) return setErrors(errs);

    mutation.mutate({
      ...formData,
      asset_worth: parseFloat(formData.asset_worth),
      duration_in_years: parseFloat(formData.duration_in_years),
      installment_duration_days: parseInt(formData.installment_duration_days),
      deferred_payment_premium_charges: parseFloat(formData.deferred_payment_premium_charges),
      available_quantity: parseInt(formData.available_quantity),
      number_of_installments: calculations.numberOfInstallments,
      net_pay_per_installment: calculations.netPayPerInstallment,
      pay_per_installment: calculations.netPayPerInstallment,
      total_pay_per_cycle: calculations.totalPayPerCycle,
      payment_without_rate: calculations.paymentWithoutRate,
    });
  }, [formData, calculations, validateForm, mutation]);

  const handleUpdateSubmit = useCallback((e) => {
    e.preventDefault();
    const errs = validateForm(updateFormData, true);
    if (Object.keys(errs).length) return setUpdateErrors(errs);
    if (!selectedItem?.id) return;

    updateMutation.mutate({
      id: selectedItem.id,
      data: {
        ...updateFormData,
        asset_worth: parseFloat(updateFormData.asset_worth),
        duration_in_years: parseFloat(updateFormData.duration_in_years),
        installment_duration_days: parseInt(updateFormData.installment_duration_days),
        deferred_payment_premium_charges: parseFloat(updateFormData.deferred_payment_premium_charges),
        available_quantity: parseInt(updateFormData.available_quantity),
        number_of_installments: updateCalculations.numberOfInstallments,
        net_pay_per_installment: updateCalculations.netPayPerInstallment,
        pay_per_installment: updateCalculations.netPayPerInstallment,
        total_pay_per_cycle: updateCalculations.totalPayPerCycle,
        payment_without_rate: updateCalculations.paymentWithoutRate,
      },
    });
  }, [updateFormData, updateCalculations, validateForm, selectedItem, updateMutation]);

  const handleOpenUpdate = useCallback((item) => {
    setSelectedItem(item);
    setUpdateFormData({
      asset_name: item.asset_name || '',
      description: item.description || '',
      condition: item.condition || 'new',
      category_ref_id: item.category_ref?.id || '',
      unit_id: item.unit?.id || '',
      asset_worth: item.asset_worth || '',
      duration_in_years: item.duration_in_years || '',
      installment_duration_days: item.installment_duration_days || '',
      deferred_payment_premium_charges: item.deferred_payment_premium_charges || '',
      available_quantity: item.available_quantity || '1',
      images: [], // empty — only send if user picks new ones
    });
    setUpdateErrors({});
    setOpen({ create: false, update: true });
  }, []);

  const goBack = useCallback(() => {
    setOpen({ create: false, update: false });
    setFormData(EMPTY_FORM);
    setUpdateFormData(EMPTY_FORM);
    setErrors({});
    setUpdateErrors({});
  }, []);

  // ── Render guards ──
  if (isLoading) return <LoadingState message="Loading assets…" />;
  if (isError) return <ErrorState onRetry={refetch} />;

  // ── Create / first-time screen ──
  if (open.create || (assets?.length === 0 && !open.update)) {
    return (
      <div>
        <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
        {open.create && <BackButton onClick={goBack} label="Back to Assets" />}
        <div className="mb-4">
          <h2 className="text-base font-bold text-[#001f3f] uppercase tracking-wide mb-1">
            {assets?.length === 0 ? 'Create Your First Asset' : 'Add New Asset'}
          </h2>
          <p className="text-xs text-gray-500">Fill in the details below to register a new asset</p>
        </div>
        <AssetForm
          isUpdate={false}
          formData={formData}
          errors={errors}
          calculations={calculations}
          categories={categories}
          units={units}
          onFieldChange={handleFieldChange}
          onImagesChange={handleImagesChange}
          onSubmit={handleSubmit}
          isLoading={mutation.isLoading}
        />
      </div>
    );
  }

  // ── Update screen ──
  if (open.update) {
    return (
      <div>
        <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
        <BackButton onClick={goBack} label="Back to Assets" />
        <div className="mb-4">
          <h2 className="text-base font-bold text-[#001f3f] uppercase tracking-wide mb-1">Update Asset</h2>
          <p className="text-xs text-gray-500">Modify the asset details below</p>
        </div>
        <AssetForm
          isUpdate
          formData={updateFormData}
          errors={updateErrors}
          calculations={updateCalculations}
          categories={categories}
          units={units}
          onFieldChange={handleUpdateFieldChange}
          onImagesChange={handleUpdateImagesChange}
          onSubmit={handleUpdateSubmit}
          isLoading={updateMutation.isLoading}
        />
      </div>
    );
  }

  // ── Stats ──
  const totalAssets = assets?.length || 0;
  const mobileAssets = assets?.filter((a) => (a.category_ref?.asset_type || a.category) === 'Mobile').length || 0;
  const immobileAssets = assets?.filter((a) => (a.category_ref?.asset_type || a.category) === 'Immobile').length || 0;
  const totalValue = assets?.reduce((s, a) => s + (Number(a.asset_worth) || 0), 0) || 0;

  // ── List view ──
  return (
    <div>
      <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
      <ConfirmModal
        open={deleteConfirm.open}
        title="Deactivate Asset"
        message="This will soft-delete the asset (is_active = false). Historical purchases will keep working. Continue?"
        confirmLabel="Yes, Deactivate"
        danger
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard accent={URITHI.navy}  label="Total Assets"  value={totalAssets}    icon={TrendingUp} />
        <StatCard accent={URITHI.green} label="Mobile"        value={mobileAssets}   icon={Car} />
        <StatCard accent="#2563eb"      label="Immobile"      value={immobileAssets} icon={HomeIcon} />
        <StatCard accent={URITHI.gold}  label="Total Value"   value={`TZS ${totalValue.toLocaleString('en-TZ')}`} icon={DollarSign} small />
      </div>

      {/* Toolbar + Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-sm font-bold text-[#001f3f] uppercase tracking-wide">Assets Overview</h2>
              <button
                onClick={() => setOpen({ create: true, update: false })}
                className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-3.5 py-2 rounded-full text-xs font-bold transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Asset
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search assets…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-56 bg-gray-50"
                />
              </div>

              {/* Type filter pills */}
              <div className="flex bg-gray-50 rounded-xl border border-gray-200 p-0.5">
                {['all', 'Mobile', 'Immobile'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setFilterType(t)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors ${
                      filterType === t ? 'bg-[#001f3f] text-white' : 'text-gray-600 hover:text-[#001f3f]'
                    }`}
                  >
                    {t === 'all' ? 'All' : t}
                  </button>
                ))}
              </div>

              {/* Condition filter */}
              <div className="relative">
                <select
                  value={filterCondition}
                  onChange={(e) => setFilterCondition(e.target.value)}
                  className="pl-3 pr-9 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] appearance-none bg-gray-50 font-medium"
                >
                  <option value="all">All conditions</option>
                  <option value="new">New</option>
                  <option value="used">Second Hand</option>
                  <option value="refurb">Refurbished</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="pl-3 pr-9 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] appearance-none bg-gray-50 font-medium"
                >
                  <option value="date">Sort by Date</option>
                  <option value="asset_name">Sort by Name</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Asset', 'Category', 'Condition', 'Qty', 'Worth (TZS)', 'Duration', 'Installments', 'DPPc', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedAssets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset) => {
                const primaryImg = asset.primary_image
                  || (asset.images?.[0]?.image ? `${IMG_BASE_URL}${asset.images[0].image}` : null)
                  || (asset.asset_image ? `${IMG_BASE_URL}${asset.asset_image}` : null);
                const imageCount = asset.images?.length || (asset.asset_image ? 1 : 0);
                const categoryName = asset.category_ref?.name || asset.category;
                const assetType = asset.category_ref?.asset_type || asset.category;
                const unitSymbol = asset.unit?.symbol || asset.unit?.name || '';

                return (
                  <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {primaryImg ? (
                            <img src={primaryImg} alt={asset.asset_name} className="w-12 h-12 rounded-xl object-cover border border-gray-200 bg-gray-50" />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          {imageCount > 1 && (
                            <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-[#001f3f] text-white text-[9px] font-bold">
                              {imageCount}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 max-w-[220px]">
                          <p className="text-xs font-bold text-[#001f3f] truncate">{asset.asset_name}</p>
                          {asset.description && (
                            <p className="text-[10px] text-gray-500 truncate">{asset.description}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-gray-700">{categoryName || '—'}</span>
                        <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                          assetType === 'Mobile'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {assetType}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge status={asset.condition === 'refurb' ? 'Refurb' : (asset.condition || 'new')} />
                    </td>

                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${
                        asset.available_quantity > 10 ? 'text-emerald-600'
                          : asset.available_quantity > 0 ? 'text-amber-600'
                          : 'text-red-600'
                      }`}>
                        {asset.available_quantity || 0}
                      </span>
                      {unitSymbol && <span className="text-[10px] text-gray-500 ml-1">{unitSymbol}</span>}
                    </td>

                    <td className="px-4 py-3 text-xs font-bold text-gray-900">{Number(asset.asset_worth)?.toLocaleString()}</td>

                    <td className="px-4 py-3 text-xs font-medium text-gray-700">
                      {asset.duration_display || `${asset.duration_in_years} yrs`}
                    </td>

                    <td className="px-4 py-3 text-xs font-bold text-[#001f3f]">{asset.number_of_installments}</td>

                    <td className="px-4 py-3 text-xs font-medium text-amber-600">
                      {(asset.deferred_payment_premium_charges * 100).toFixed(1)}%
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <IconBtn onClick={() => handleOpenUpdate(asset)} title="Edit" color="navy">
                          <Edit2 className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn onClick={() => setDeleteConfirm({ open: true, id: asset.id })} title="Deactivate" color="red">
                          <Trash2 className="w-3.5 h-3.5" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={sortedAssets.length}
          onPageChange={setPage}
          onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
        />
      </div>
    </div>
  );
}

export default UrithiNormalAssets;